import React, { useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import Grid from '@material-ui/core/Grid';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import Button from '@material-ui/core/Button';
import DialogTitle from '@material-ui/core/DialogTitle';
import { COMPLETE, SAVING } from '../hooks/useJobActions';
import useJobForm from '../hooks/useJobForm';
import Form from '../../form/Form';
import CloseJobFields from './CloseJobFields';
import { defaultJobCloseValues } from '../assets/schemas/add/addSchema';
import model from '../assets/models/closeJobModel';

const useStyles = makeStyles((theme) => ({
  dialogActions: {
    margin: theme.spacing(2),
  },
  summary: {
    color: theme.palette.grey[600],
  },
}));

const CloseJobDialog = ({ onComplete, onClose, open, job, disabled }) => {
  const classes = useStyles();
  const { methods, actions } = useJobForm(model, defaultJobCloseValues);
  const { close, status } = actions;
  const { reset, setValue } = methods;

  const localOnClose = useCallback(() => {
    reset(defaultJobCloseValues);
    onClose();
  }, [onClose, reset]);

  useEffect(() => {
    if (status === COMPLETE) {
      onComplete();
      localOnClose();
    }
  }, [localOnClose, onComplete, status]);

  const cancel = useCallback(() => {
    localOnClose();
  }, [localOnClose]);

  useEffect(() => {
    if (open) {
      setValue('id', job.id);
      setValue('lastUpdate', job.lastUpdate);
    }
  }, [open, setValue, job]);

  return (
    <Dialog open={open} onClose={localOnClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <b>Close Job Posting</b>
      </DialogTitle>
      <Form methods={methods}>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <span className={classes.summary}>
                You are about to close the following posting{' '}
                <b>{` ${job.jobId}: ${job.jobTitle}`}</b>. Please review the
                close out information below.
              </span>
            </Grid>
            <CloseJobFields />
          </Grid>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button onClick={cancel}>Cancel</Button>
          {!disabled && (
            <Button
              onClick={close}
              color="primary"
              variant="contained"
              disabled={status === SAVING}
            >
              Close Post
            </Button>
          )}
        </DialogActions>
      </Form>
    </Dialog>
  );
};

CloseJobDialog.defaultProps = {
  job: {},
  disabled: false,
};

CloseJobDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  job: PropTypes.object,
  disabled: PropTypes.bool,
};

export default CloseJobDialog;
